import React, { useState, useEffect, useRef } from "react";
import * as pdfjsLib from "pdfjs-dist";
import {
  Loader2,
  Download,
  FileText,
  X,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Maximize2,
  Minimize2,
  Star,
  Send,
  ArrowLeft,
  MessageSquare,
  Trash2,
  Smile,
  Frown,
} from "lucide-react";
import axios from "axios";

pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.mjs`;

const SpocCertificateDownload = ({ talk, onClose, authToken }) => {
  const [loading, setLoading] = useState(false);
  const [pdfBase64, setPdfBase64] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [pdfUrl, setPdfUrl] = useState("");
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showFeedback, setShowFeedback] = useState(true);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [sessionRating, setSessionRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedbackError, setFeedbackError] = useState("");
  const [feedbackSuccess, setFeedbackSuccess] = useState("");

  // For ratings 1-2 (dislikes)
  const [dislikes, setDislikes] = useState([""]);
  const [suggestionLow, setSuggestionLow] = useState("");

  // For ratings 3-5 (likes) - ALWAYS 3 fields
  const [likes, setLikes] = useState(["", "", ""]);
  const [suggestionHigh, setSuggestionHigh] = useState("");

  const iframeRef = useRef(null);

  // const API_BASE = 'http://192.168.2.212:8001';
  const API_BASE = "https://erpapi.eduskillsfoundation.org";

  // Check if feedback already submitted
  const isFeedbackSubmitted = talk.spoc_enrollment?.is_feedback === "1";

  // ==================== FEEDBACK FUNCTIONS ====================
  const handleRatingClick = (rating) => {
    setSessionRating(rating);
    setFeedbackError(""); // Clear any previous errors

    // Reset form fields when rating changes
    if (rating < 3) {
      // Reset to 1 field for dislikes
      setDislikes([""]);
      setSuggestionLow("");
    } else {
      // Always ensure 3 fields for likes
      setLikes(["", "", ""]);
      setSuggestionHigh("");
    }
  };

  const handleAddDislikeField = () => {
    if (dislikes.length < 5) {
      setDislikes([...dislikes, ""]);
    }
  };

  const handleRemoveDislikeField = (index) => {
    if (dislikes.length > 1) {
      const newDislikes = [...dislikes];
      newDislikes.splice(index, 1);
      setDislikes(newDislikes);
    }
  };

  const handleDislikeChange = (index, value) => {
    const newDislikes = [...dislikes];
    newDislikes[index] = value;
    setDislikes(newDislikes);
  };

  const handleLikeChange = (index, value) => {
    const newLikes = [...likes];
    newLikes[index] = value;
    setLikes(newLikes);
  };

  const validateFeedback = () => {
    // Check if rating is selected
    if (sessionRating === 0) {
      setFeedbackError("Please select a rating by clicking on the stars");
      return false;
    }

    // For low ratings (1-2)
    if (sessionRating < 3) {
      // Validate dislikes - at least one valid dislike required
      const validDislikes = dislikes.filter((dislike) => dislike.trim() !== "");
      if (validDislikes.length === 0) {
        setFeedbackError(
          "Please mention at least one thing that could be improved"
        );
        return false;
      }

      // Validate each dislike field
      for (let i = 0; i < validDislikes.length; i++) {
        if (validDislikes[i].trim().length < 5) {
          setFeedbackError(
            `Improvement point #${i + 1} should be at least 5 characters long`
          );
          return false;
        }
      }
    } else {
      // For high ratings (3-5)
      // Validate likes - at least one field should be filled
      const validLikes = likes.filter((like) => like.trim() !== "");

      if (validLikes.length === 0) {
        setFeedbackError(
          "Please fill at least one of the 'What you liked' fields"
        );
        return false;
      }

      // Validate each filled like field
      for (let i = 0; i < likes.length; i++) {
        if (likes[i].trim() !== "" && likes[i].trim().length < 5) {
          setFeedbackError(
            `Like point #${i + 1} should be at least 5 characters long`
          );
          return false;
        }
      }
    }

    // Optional suggestion validation
    const suggestion = sessionRating < 3 ? suggestionLow : suggestionHigh;
    if (suggestion && suggestion.trim().length < 5) {
      setFeedbackError("Suggestion should be at least 5 characters long");
      return false;
    }

    return true;
  };

  const handleSubmitFeedback = async () => {
    if (!validateFeedback()) {
      return;
    }

    setFeedbackLoading(true);
    setFeedbackError("");
    setFeedbackSuccess("");

    try {
      // Filter out empty strings and trim
      const filteredDislikes = dislikes
        .map((d) => d.trim())
        .filter((d) => d !== "");

      const filteredLikes = likes.map((l) => l.trim()).filter((l) => l !== "");

      // Prepare data according to API
      const data = {
        rating: sessionRating,
        good_things: sessionRating >= 3 ? filteredLikes : null,
        bad_thing: sessionRating < 3 ? filteredDislikes.join("; ") : null,
        suggestion:
          sessionRating < 3 ? suggestionLow || null : suggestionHigh || null,
      };

      const instituteId = talk.spoc_enrollment?.institute_id;
      const feedbackUrl = `${API_BASE}/new_support/career_talk/rating_feedback/${talk.id}/${instituteId}`;

      const response = await axios.post(feedbackUrl, data, {
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
      });

      if (response.data.message && response.data.feedback) {
        setFeedbackSuccess("Thank you for your valuable feedback!");
        setFeedbackSubmitted(true);

        // Update the talk object to reflect feedback submission
        if (talk.spoc_enrollment) {
          talk.spoc_enrollment.is_feedback = "1";
        }

        setTimeout(() => {
          setShowFeedback(false);
          fetchCertificate();
        }, 1500);
      } else {
        throw new Error("Invalid response from server");
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      setFeedbackError(
        error.response?.data?.message ||
          error.message ||
          "Failed to submit feedback"
      );
    } finally {
      setFeedbackLoading(false);
    }
  };

  // ==================== FETCH CERTIFICATE ====================
  const fetchCertificate = async () => {
    setLoading(true);
    setError("");
    setSuccess("");
    setPdfBase64("");
    setPdfUrl("");

    try {
      if (talk.isCertificateIssued !== true) {
        throw new Error("Certificate not yet issued. Please contact support.");
      }

      if (!talk.id) {
        throw new Error("Career Talk ID not found");
      }

      const instituteId = talk.spoc_enrollment?.institute_id;

      if (!instituteId) {
        const enrollment = talk.spoc_enrollment;
        if (enrollment && enrollment.institute_id) {
          console.log(
            "Found institute_id in enrollment:",
            enrollment.institute_id
          );
        } else {
          throw new Error(`Institute ID not found.`);
        }
      }

      const fetchUrl = `${API_BASE}/new_support/career_talk/certificate_fetch/${talk.id}/${instituteId}`;

      const response = await axios.get(fetchUrl, {
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
      });

      const data = response.data;

      let pdfData = null;

      if (data.certificate?.pdf_base64) {
        pdfData = data.certificate.pdf_base64;
      } else if (data.pdf_base64) {
        pdfData = data.pdf_base64;
      } else if (data.certificate?.certificate_file_url) {
        const pdfUrl = data.certificate.certificate_file_url;
        setPdfUrl(pdfUrl);
        setSuccess("Certificate loaded successfully!");
        setLoading(false);
        return;
      } else if (data.certificate_url) {
        const pdfUrl = data.certificate_url;
        setPdfUrl(pdfUrl);
        setSuccess("Certificate loaded successfully!");
        setLoading(false);
        return;
      }

      if (pdfData) {
        await processPdfBase64(pdfData);
        setSuccess("Certificate loaded successfully!");
      } else {
        setError(
          "Certificate PDF not available in response. Please contact support."
        );
      }
    } catch (error) {
      console.error("Error fetching certificate:", error);
      const errorMsg =
        error.response?.data?.message ||
        error.response?.data?.detail ||
        error.message ||
        "Failed to load certificate";
      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // ==================== PROCESS PDF BASE64 ====================
  const processPdfBase64 = async (base64String) => {
    try {
      let cleanBase64 = base64String.trim();

      if (cleanBase64.startsWith("data:application/pdf;base64,")) {
        cleanBase64 = cleanBase64.replace("data:application/pdf;base64,", "");
      }

      setPdfBase64(cleanBase64);

      const byteCharacters = atob(cleanBase64);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (error) {
      console.error("Error processing PDF:", error);
      throw error;
    }
  };

  // ==================== DOWNLOAD PDF ====================
  const handleDownloadPDF = () => {
    if (!pdfBase64 && !pdfUrl) {
      setError("No certificate available to download");
      return;
    }

    try {
      if (pdfBase64) {
        const byteCharacters = atob(pdfBase64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });

        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;

        const sanitize = (str) => str.replace(/[^a-z0-9]/gi, "_").toLowerCase();
        const fileName = `${sanitize(
          talk.title
        )}_certificate_${Date.now()}.pdf`;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } else if (pdfUrl) {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.target = "_blank";

        const sanitize = (str) => str.replace(/[^a-z0-9]/gi, "_").toLowerCase();
        const fileName = `${sanitize(
          talk.title
        )}_certificate_${Date.now()}.pdf`;
        link.download = fileName;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }

      setSuccess("Certificate downloaded successfully!");
    } catch (error) {
      console.error("Download failed:", error);
      setError("Download failed: " + error.message);
    }
  };

  // ==================== TOGGLE FULLSCREEN ====================
  const toggleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Show feedback modal on mount
  useEffect(() => {
    if (talk.isCertificateIssued === true) {
      if (isFeedbackSubmitted) {
        setShowFeedback(false);
        fetchCertificate();
      } else {
        setShowFeedback(true);
      }
    } else {
      setShowFeedback(false);
      fetchCertificate();
    }
  }, []);

  // Clean up URL on unmount
  useEffect(() => {
    return () => {
      if (pdfUrl && pdfUrl.startsWith("blob:")) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  // Reusable Button Component
  const Button = ({
    onClick,
    variant = "default",
    size = "default",
    className = "",
    disabled = false,
    children,
  }) => {
    const baseClasses =
      "inline-flex items-center justify-center rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";
    const variantClasses = {
      default: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
      outline:
        "border border-gray-300 bg-white hover:bg-gray-50 focus:ring-blue-500",
      ghost: "hover:bg-gray-100 focus:ring-gray-500",
    };
    const sizeClasses = {
      default: "h-10 px-4 py-2 text-sm sm:text-base",
      sm: "h-8 px-3 text-xs sm:text-sm",
      lg: "h-12 px-6 text-base sm:text-lg",
    };

    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      >
        {children}
      </button>
    );
  };

  // ==================== RENDER LOW RATING FORM (1-2 stars) ====================
  const renderLowRatingForm = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Dislikes Section */}
      <div>
        <div className="flex items-start sm:items-center gap-2 mb-3">
          <Frown className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div>
            <h3 className="text-base font-medium text-gray-900">
              What could be improved?
            </h3>
            {/* <p className="text-sm text-gray-600 mt-1">
              Your honest feedback helps us improve future sessions
            </p> */}
          </div>
        </div>

        <div className="space-y-3">
          {dislikes.map((dislike, index) => (
            <div key={index} className="flex gap-2 items-start">
              <input
                type="text"
                value={dislike}
                onChange={(e) => handleDislikeChange(index, e.target.value)}
                placeholder={`Improvement point ${index + 1}`}
                className="flex-1 p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base"
              />
              {dislikes.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveDislikeField(index)}
                  className="p-3 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-md flex-shrink-0"
                  aria-label="Remove field"
                >
                  <Trash2 className="h-5 w-5" />
                </button>
              )}
            </div>
          ))}
        </div>

        {/* {dislikes.length < 5 && (
          <button
            type="button"
            onClick={handleAddDislikeField}
            className="mt-3 inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 text-sm"
          >
            <span className="text-lg">+</span>
            Add another improvement point
          </button>
        )} */}
      </div>

      {/* Suggestion Section */}
      <div>
        <div className="flex items-start sm:items-center gap-2 mb-3">
          <MessageSquare className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div>
            <h3 className="text-base font-medium text-gray-900">
              Any suggestions for improvement?
            </h3>
          </div>
        </div>

        <textarea
          value={suggestionLow}
          onChange={(e) => setSuggestionLow(e.target.value)}
          placeholder="Your suggestions for making this session better..."
          rows={3}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm sm:text-base"
        />
      </div>
    </div>
  );

  // ==================== RENDER HIGH RATING FORM (3-5 stars) ====================
  const renderHighRatingForm = () => (
    <div className="space-y-4 sm:space-y-6">
      {/* Likes Section - Always 3 fields */}
      <div>
        <div className="flex items-start sm:items-center gap-2 mb-3">
          <Smile className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div>
            <h3 className="text-base font-medium text-gray-900">
              What did you like about the session?
            </h3>
            {/* <p className="text-sm text-gray-600 mt-1">
              You can fill 1, 2, or all 3 fields - fields are optional but at least one is required
            </p> */}
          </div>
        </div>

        <div className="space-y-3">
          {likes.map((like, index) => (
            <div key={index} className="relative">
              <input
                type="text"
                value={like}
                onChange={(e) => handleLikeChange(index, e.target.value)}
                placeholder={`Like point ${index + 1} `}
                className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm sm:text-base pr-10"
              />
              {like.trim() !== "" && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500">
                  ✓
                </div>
              )}
            </div>
          ))}
        </div>

        {/* <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-xs text-gray-500">
          <div className="bg-gray-50 p-2 rounded">
            <span className="font-medium">Field 1:</span> Speaker/Content
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <span className="font-medium">Field 2:</span> Organization
          </div>
          <div className="bg-gray-50 p-2 rounded">
            <span className="font-medium">Field 3:</span> Overall Experience
          </div>
        </div> */}
      </div>

      {/* Suggestion Section */}
      <div>
        <div className="flex items-start sm:items-center gap-2 mb-3">
          <MessageSquare className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5 sm:mt-0" />
          <div>
            <h3 className="text-base font-medium text-gray-900">
              Any suggestions for future sessions? (Optional)
            </h3>
          </div>
        </div>

        <textarea
          value={suggestionHigh}
          onChange={(e) => setSuggestionHigh(e.target.value)}
          placeholder="Your suggestions for future sessions..."
          rows={3}
          className="w-full p-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none text-sm sm:text-base"
        />
      </div>
    </div>
  );

  // ==================== RENDER FEEDBACK MODAL ====================
  const renderFeedbackModal = () => (
    <div className="flex flex-col h-full max-h-[90vh] sm:max-h-[85vh] lg:max-h-[80vh]">
      {/* Header for mobile */}
      <div className="sm:hidden flex items-center justify-between pb-3 border-b px-4 pt-4 flex-shrink-0">
        <div className="flex items-center gap-2">
          <MessageSquare className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-semibold text-gray-900">Rate Session</h3>
        </div>
        <button
          onClick={() => {
            setShowFeedback(false);
            onClose();
          }}
          className="p-1 hover:bg-gray-100 rounded-md transition-colors"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {/* Desktop Header */}
      <div className="hidden sm:block text-center px-6 pt-4 sm:pt-5 lg:pt-6 pb-3 sm:pb-4 flex-shrink-0">
        <h3 className="text-xl lg:text-2xl font-semibold text-gray-900 mb-1.5 sm:mb-2">
          Rate Your Experience
        </h3>
        <p className="text-sm lg:text-base text-gray-600">
          Please rate the session before downloading your certificate
        </p>
      </div>

      {/* Scrollable Content Area */}
      <div className="flex-1 overflow-y-auto min-h-0 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">
          {/* Feedback Messages */}
          {feedbackError && (
            <div className="mb-3 sm:mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
                <p className="text-red-700 text-sm">{feedbackError}</p>
              </div>
            </div>
          )}

          {feedbackSuccess && (
            <div className="mb-3 sm:mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
                <p className="text-green-700 text-sm">{feedbackSuccess}</p>
              </div>
            </div>
          )}

          {/* Star Rating Section */}
          <div className="flex flex-col items-center justify-center py-4 sm:py-6 lg:py-8">
            {/* Star Rating */}
            <div className="flex items-center justify-center gap-1 sm:gap-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  type="button"
                  onClick={() => handleRatingClick(star)}
                  onMouseEnter={() => setHoverRating(star)}
                  onMouseLeave={() => setHoverRating(0)}
                  className="p-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 rounded transition-transform hover:scale-110 active:scale-95"
                  aria-label={`Rate ${star} star${star !== 1 ? "s" : ""}`}
                >
                  <Star
                    className={`h-10 w-10 sm:h-11 sm:w-11 lg:h-12 lg:w-12 transition-colors ${
                      star <= (hoverRating || sessionRating)
                        ? "fill-yellow-400 text-yellow-400"
                        : "fill-gray-200 text-gray-300"
                    }`}
                  />
                </button>
              ))}
            </div>
            {sessionRating > 0 && (
              <p className="mt-3 text-sm text-gray-600">
                You rated {sessionRating} star{sessionRating !== 1 ? "s" : ""}
              </p>
            )}
          </div>

          {/* Dynamic Form Section */}
          {sessionRating > 0 && (
            <div className="pb-4 sm:pb-6">
              {sessionRating < 3
                ? renderLowRatingForm()
                : renderHighRatingForm()}
            </div>
          )}
        </div>
      </div>

      {/* Submit Button Section - Fixed at bottom */}
      <div className="flex-shrink-0 border-t bg-white shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)]">
        <div className="flex flex-col sm:flex-row justify-end gap-2.5 sm:gap-3 px-4 sm:px-6 py-3.5 sm:py-4 lg:py-5 max-w-2xl ml-auto w-full">
          {sessionRating === 0 ? (
            <Button
              onClick={() => {
                setShowFeedback(false);
                onClose();
              }}
              variant="outline"
              className="w-full sm:w-auto sm:min-w-[140px] h-10 sm:h-11"
            >
              Skip Feedback
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setShowFeedback(false);
                  onClose();
                }}
                variant="outline"
                className="w-full sm:w-auto sm:min-w-[120px] h-10 sm:h-11 order-2 sm:order-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmitFeedback}
                disabled={feedbackLoading}
                className="w-full sm:w-auto sm:min-w-[200px] h-10 sm:h-11 order-1 sm:order-2"
              >
                {feedbackLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit & View Certificate
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ==================== RENDER CERTIFICATE MODAL ====================
  const renderCertificateModal = () => (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b p-3 sm:p-4 flex justify-between items-center bg-gray-50">
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-blue-600" />
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 truncate">
            Certificate - {talk.title}
          </h2>
        </div>
        <div className="flex items-center gap-1 sm:gap-2">
          {/* Only show Edit Feedback button if feedback was not already submitted */}
          {!isFeedbackSubmitted && (
            <Button
              onClick={() => setShowFeedback(true)}
              variant="outline"
              size="sm"
              className="text-blue-600 hover:text-blue-700 hidden sm:inline-flex"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Edit Feedback
            </Button>
          )}
          <Button
            onClick={toggleFullscreen}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-gray-200 rounded-md"
            title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
          >
            {isFullscreen ? (
              <Minimize2 className="h-4 w-4" />
            ) : (
              <Maximize2 className="h-4 w-4" />
            )}
          </Button>
          <Button
            onClick={onClose}
            variant="ghost"
            size="sm"
            className="h-8 w-8 p-0 hover:bg-gray-200 rounded-md"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Mobile Edit Feedback Button */}
      {!isFeedbackSubmitted && (
        <div className="sm:hidden border-b p-3">
          <button
            onClick={() => setShowFeedback(true)}
            className="w-full py-2 border border-blue-600 text-blue-600 rounded-md hover:bg-blue-50 flex items-center justify-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Edit Feedback
          </button>
        </div>
      )}

      {/* Content */}
      <div className="flex flex-col h-[calc(100%-120px)] sm:h-[calc(100%-140px)]">
        {/* Messages */}
        {error && (
          <div className="m-3 sm:m-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-start">
              <AlertCircle className="h-5 w-5 text-red-400 mt-0.5 mr-2 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-red-700 text-sm font-medium">Error</p>
                <p className="text-red-600 text-sm mt-1">{error}</p>
              </div>
            </div>
          </div>
        )}

        {success && (
          <div className="m-3 sm:m-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <div className="flex items-center">
              <CheckCircle className="h-5 w-5 text-green-400 mr-2 flex-shrink-0" />
              <p className="text-green-700 text-sm">{success}</p>
            </div>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-8 sm:py-12 flex-1">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600 mb-3 sm:mb-4" />
            <p className="text-gray-600 text-sm sm:text-base">
              Loading certificate...
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mt-1 sm:mt-2 text-center px-4">
              Please wait while we fetch your certificate
            </p>
          </div>
        )}

        {/* Certificate Display */}
        {!loading && pdfUrl && (
          <div className="flex-1 overflow-hidden">
            <div className="p-2 sm:p-4 h-full">
              <div className="border border-gray-300 rounded-lg overflow-hidden h-full bg-gray-50">
                <iframe
                  ref={iframeRef}
                  src={`${pdfUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  title="Certificate Preview"
                  className="w-full h-full min-h-[300px] sm:min-h-[400px]"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        )}

        {/* Nothing Available */}
        {!loading && !pdfUrl && !error && (
          <div className="flex flex-col items-center justify-center py-8 sm:py-12 flex-1">
            <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 text-gray-300 mb-3 sm:mb-4" />
            <p className="text-gray-600 text-sm sm:text-base mb-1 sm:mb-2">
              Certificate not available
            </p>
            <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 text-center px-4">
              The certificate may not have been issued yet or there was an error
              loading it.
            </p>
            <Button
              onClick={fetchCertificate}
              variant="outline"
              size="sm"
              className="mt-2 sm:mt-4"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Try Again
            </Button>
          </div>
        )}
      </div>

      {/* Footer with Download Button */}
      <div className="border-t p-3 sm:p-4 bg-gray-50">
        <div className="flex justify-end items-center">
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            {pdfUrl && (
              <Button
                onClick={handleDownloadPDF}
                className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 px-4 sm:px-6"
              >
                <Download className="h-4 w-4 mr-2" />
                Download Certificate
              </Button>
            )}
            <Button
              onClick={onClose}
              variant="outline"
              disabled={loading}
              className="w-full sm:w-auto px-4 sm:px-6"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-2 sm:p-4 z-[9999]">
      <div
        className={`bg-white rounded-lg shadow-xl ${
          isFullscreen
            ? "w-full h-full"
            : "w-full max-w-4xl max-h-[90vh] sm:max-h-[95vh]"
        } overflow-hidden transition-all duration-300`}
      >
        {showFeedback ? renderFeedbackModal() : renderCertificateModal()}
      </div>
    </div>
  );
};

export default SpocCertificateDownload;
